
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Loading from '~/components/Loading';
import AssistantMessage from './AssistantMessage';
import AssistantInput from './AssistantInput';
import AssistantIdeas from './AssistantIdeas';
import {
    clearOnboardingBookingUrls,
    getOnboardingBookingUrls,
    importBookingPagesPrompt,
} from '~/lib/OnboardingImport';
import AssistantRecentConvos from './AssistantRecentConvos';
import useBusinessPostApi from '~/lib/useBusinessPostApi.js';
import useBackgroundTaskResults from '~/lib/useBackgroundTaskResults.js';
import { ALL, parse } from 'partial-json';
import * as Toast from '~/lib/Toast';
import { invalidateQueries, useBusinessInfo } from '~/lib/Queries';
import { ensureObjectArray } from '~/lib/Utils.js';
import AssistantFeedback from '~/components/assistant/AssistantFeedback.jsx';

/**
 * Messages have no server-side id, and the message list is re-sent whole on every poll
 * (and truncated once the conversation gets long), so an index key lets React reuse a
 * component instance for a different message. Key tool call messages by the ids they
 * hold so an instance is never reused across a different set of tool calls.
 */
function messageKey(message, idx) {
    const toolCalls = message.tool_calls
        ? ensureObjectArray(message.tool_calls)
        : null;
    if (toolCalls?.length > 0) {
        return `tools-${toolCalls.map(t => t.id).join('-')}`;
    }

    if (message.role === 'tool') {
        const results = message.content ? ensureObjectArray(message.content) : null;
        if (results?.length > 0) {
            return `results-${results.map(r => r.toolCallId).join('-')}`;
        }
    }

    return `${idx}-${message.role}`;
}

// Distinguishes runs the app started by itself from ones the user typed, so they can be
// told apart in the assistant log and in usage metrics.
const AUTO_IMPORT_SOURCE = 'AssistantChatboxAutoImport';

const AUTO_IMPORT_FAILED_MESSAGE =
    "I wasn't able to import your booking page automatically. You can tell me about your "
    + 'services and I\'ll set them up for you.'
    + '\n\n<RECOMMENDED>Set up my services</RECOMMENDED>\n<RECOMMENDED>Set my availability</RECOMMENDED>';

function hasToolResponse(message) {
    if (!Array.isArray(message.content)) {
        return false;
    }
    return message.content.every(r => r.result);
}

const AssistantChatConversation = forwardRef(({
    showChat,
    isNewBusiness,
    hideRecentConvos,
    hideIdeasAndInput,
    setShowChat,
    onMessagesUpdate,
    onShowIdeasChange,
    context,
    isSideColumn,
    source,
    className,
    placeholders,
    // What the chat opens on instead of the ideas, ex. an editor's greeting
    startingMessages,
}, ref) => {
    // Booking pages given during onboarding. When there are any we import from them
    // automatically instead of waiting for the business to ask.
    const [onboardingImportPrompt] = useState(
        () => importBookingPagesPrompt(getOnboardingBookingUrls().urls),
    );
    const initialMessages = isNewBusiness
        ? [
              {
                  role: 'assistant',
                  content:
                      //"**Welcome to Breely!** 🎉\n\nI'm an expert in Breely and can set everything up for you so you can start booking clients right away.\n\nI can walk you through setup, or answer any questions while you explore.\n\nWhat would you like to do?\n\n<RECOMMENDED>Set up my services</RECOMMENDED>\n<RECOMMENDED>Configure from my website</RECOMMENDED>\n<RECOMMENDED>Set my availability</RECOMMENDED>\n<RECOMMENDED>Walk me through the full setup</RECOMMENDED>",
                    onboardingImportPrompt
                        ? "**Welcome to Breely!** 🎉\n\nI'm reading your booking page and setting up your services and forms now. This will take a minute."
                        : "**Welcome to Breely!** 🎉\n\nWhat would you like to do to get started?\n\n<RECOMMENDED>Set up my services</RECOMMENDED>\n<RECOMMENDED>Set my availability</RECOMMENDED>\n<RECOMMENDED>Configure from my website</RECOMMENDED>",
              },
          ]
        : (startingMessages ?? []);
    const [messages, setMessages] = useState(initialMessages);
    const [prompt, setPrompt] = useState('');
    const [backgroundTaskToken, setBackgroundTaskToken] = useState(null);
    const assistantRef = useRef(null);
    const messagesBeforeOptimisticRef = useRef(null);
    const [hasSelectedIdea, setHasSelectedIdea] = useState(false);
    // Guards the auto import against StrictMode's double effect run in development
    const hasAutoImportedRef = useRef(false);
    // True only while the in-flight run is the one we started ourselves
    const autoImportRunningRef = useRef(false);
    const [autoImportFailed, setAutoImportFailed] = useState(false);
    const { data: businessData } = useBusinessInfo();

    const { save: saveChat, isSaving: isChatting } = useBusinessPostApi(`/assistant/chat`);

    const {
        status,
        error: submitError,
        results,
        id : backgroundTaskId,
        reset: resetBackgroundTask,
    } = useBackgroundTaskResults(backgroundTaskToken, results => {
        handleMessagesUpdate(results || messages, true);
    });

    const isRunning = isChatting || status === 'pending' || status === 'running';

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        setMessages: (newMessages) => {
            setMessages(newMessages);
        },
        getMessages: () => messages,
        submitPrompt: (thisPrompt) => {
            submitPrompt(thisPrompt);
        },
        setPrompt: (text) => {
            setPrompt(text);
        },
    }));

    function submitPrompt(thisPrompt, overrideSource = null) {
        setAutoImportFailed(false);

        const hasPriorUserMessage = messages.some(m => m.role === 'user');
        if (!hasPriorUserMessage) {
            // The import offer has been shown and acted on (or passed over), so retire it
            clearOnboardingBookingUrls();
        }

        // Optimistically add the user message to local state immediately
        messagesBeforeOptimisticRef.current = messages;
        const updatedMessages = [...messages, { role: 'user', content: thisPrompt }];
        setMessages(updatedMessages);

        return saveChat({
            prompt: thisPrompt,
            messages,
            currentUrl: document.location.pathname,
            context: context,
            source: overrideSource || source,
        }).then(result => {
            setBackgroundTaskToken(result.token);
        });
    }

    function showAutoImportFailed() {
        autoImportRunningRef.current = false;
        setAutoImportFailed(true);
    }

    // Start the import as soon as the chat opens for a business that gave us booking URLs
    // during onboarding, rather than offering it as something to click.
    useEffect(() => {
        if (!showChat || !isNewBusiness || !onboardingImportPrompt) return;
        if (hasAutoImportedRef.current) return;
        // Never inject into a conversation that already has something in it
        if (messages.some(m => m.role === 'user')) return;

        hasAutoImportedRef.current = true;
        autoImportRunningRef.current = true;
        // submitPrompt clears the stored URLs, so a reopened chat will not import twice
        submitPrompt(onboardingImportPrompt, AUTO_IMPORT_SOURCE).catch(() => {
            showAutoImportFailed();
        });
    }, [showChat, isNewBusiness, onboardingImportPrompt]);

    // The chat request is handled by a background job, so a failure shows up here rather
    // than as a rejected post
    useEffect(() => {
        if (!autoImportRunningRef.current) return;

        if (status === 'failed') {
            showAutoImportFailed();
        } else if (status === 'success') {
            autoImportRunningRef.current = false;
        }
    }, [status]);

    useEffect(() => {
        const parsed = typeof results === 'string' ? parse(results, ALL) : results;
        console.log('Setting background task results = ', parsed);
        handleMessagesUpdate(parsed || messages, false);
    }, [results]);

    useEffect(() => {
        if (showChat) {
            // Messages updated! Scroll to the bottom
            setTimeout(() => {
                const chatbox = document.querySelector('.assistant-chatbox');
                // Scroll to the bottom of the chatbox smoothly
                chatbox?.scrollTo({
                    top: chatbox.scrollHeight,
                    behavior: 'smooth',
                });
            }, 100);
        }
    }, [messages, isRunning]);

    function handleMessagesUpdate(newMessages, isFinal) {
        if (typeof newMessages?.findLastIndex !== 'function') {
            Toast.error('Something went wrong with the assistant, please try again');
            return;
        }

        messagesBeforeOptimisticRef.current = null;
        setMessages(newMessages);

        // Find if there is a tool result before the last user message
        const toolResultIdx = newMessages.findLastIndex(m => m.role === 'tool');
        const lastUserMessageIdx = newMessages.findLastIndex(m => m.role === 'user');

        if (toolResultIdx !== -1 && toolResultIdx > lastUserMessageIdx) {
            for (const toolResult of ensureObjectArray(newMessages[toolResultIdx].content)) {
                let result = {};
                try {
                    result = JSON.parse(toolResult.result);
                } catch (err) {
                    console.log('Error parsing tool result', err);
                }

                // Force a refetch for any data
                if (isFinal) {
                    invalidateQueries();
                    // Call parent callback if provided
                    if (onMessagesUpdate) {
                        onMessagesUpdate(newMessages, isFinal);
                    }
                }
            }
        }
    }

    function submit(e) {
        if (!prompt.trim()) return;
        e.preventDefault();
        submitPrompt(prompt);
        setPrompt('');
    }

    function stopRunning() {
        // Stopping on purpose is not a failure, so do not report one
        autoImportRunningRef.current = false;
        if (messagesBeforeOptimisticRef.current) {
            setMessages(messagesBeforeOptimisticRef.current);
            messagesBeforeOptimisticRef.current = null;
        }
        resetBackgroundTask();
        setBackgroundTaskToken(null);
    }

    const handleToolResponse = response => {
        if (response && response.token) {
            setBackgroundTaskToken(response.token);
        }
    };

    const displayMessages = [
        ...messages.filter(m => m.role !== 'tool' || hasToolResponse(m)),
        ...(autoImportFailed
            ? [{ role: 'assistant', content: AUTO_IMPORT_FAILED_MESSAGE }]
            : []),
    ];
    const lastMessage = displayMessages.length > 0 ? displayMessages[displayMessages.length - 1] : null;
    const showInput = !lastMessage || !lastMessage.tool_calls || !showChat;
    const hasMessages = displayMessages.length > 0 || isRunning;
    const showBusinessWelcomeMessage = isNewBusiness && !hasMessages;
    const showIdeas = messages.length === 0 &&
        !isRunning &&
        showChat;

    useEffect(() => {
        if (onShowIdeasChange) {
            onShowIdeasChange(showIdeas);
        }
    }, [showIdeas]);

    return (
        <div className={className}>
            {hasMessages && (
                <div
                    className={`${showChat ? 'overflow-y-scroll scrollbar-visible pb-4' : 'max-h-0 overflow-hidden'} assistant-chatbox transition-all duration-300`}
                >
                    {displayMessages.map((message, idx) => (
                        <AssistantMessage
                            key={messageKey(message, idx)}
                            message={message}
                            className={null}
                            messages={messages}
                            isLastMessage={
                                idx === displayMessages.length - 1
                            }
                            onToolResponse={handleToolResponse}
                            onClickRecommendedPrompt={(message) => {
                                submitPrompt(message);
                            }}
                            isRunning={isRunning}
                        />
                    ))}
                    {isRunning && (
                        // <div className="assistant-ai-message rounded-[22px] p-4 mt-4 inline-block">
                            <div className="loader-dots mt-4" />
                        // </div>
                        // <Loading
                        //     message={<span className="animate-pulse">Thinking...</span>}
                        //     className="mt-4"
                        // />
                    )}
                    {!isRunning && showInput && (
                        <AssistantFeedback
                            backgroundTaskId={backgroundTaskId}
                        />
                    )}
                </div>
            )}

            {showInput && (
                <div
                    className={
                        showChat && !showBusinessWelcomeMessage
                            ? 'mb-4'
                            : ''
                    }
                >
                    {showIdeas && isSideColumn && (
                        <div>
                            <div className="mt-6 mb-6 text-medium text-center text-lg text-gray-500">
                                🎉 Welcome to Breely! Explore your booking page and chat with the Assistant to make it your own.
                            </div>

                            <AssistantIdeas
                                className="mt-4"
                                onSelect={idea => {
                                    setPrompt(idea + ' ');
                                    assistantRef.current?.focus();
                                }}
                                hasPrompt={prompt.trim().length > 0}
                                businessData={businessData}
                            />
                        </div>
                    )}

                    {!(showIdeas && hideIdeasAndInput) && (
                        <AssistantInput
                            ref={assistantRef}
                            prompt={prompt}
                            setPrompt={setPrompt}
                            isRunning={isRunning}
                            submit={submit}
                            onStop={stopRunning}
                            onFocus={() => {}}
                            showChat={showChat}
                            isNewBusiness={isNewBusiness}
                            placeholders={placeholders}
                        />
                    )}
                    {showIdeas && !isSideColumn && (
                        <div>
                            {!hideIdeasAndInput && (
                                <AssistantIdeas
                                    className="mt-4"
                                    onSelect={idea => {
                                        setHasSelectedIdea(!hasSelectedIdea)
                                        setPrompt(idea + ' ');
                                        assistantRef.current?.focus();
                                    }}
                                    hasPrompt={prompt.trim().length > 0}
                                    businessData={businessData}
                                />
                            )}

                            {!hideRecentConvos && !hasSelectedIdea && (
                                <AssistantRecentConvos
                                    onSelect={convo => {
                                        setMessages(convo);
                                        if (setShowChat) {
                                            setShowChat(true);
                                        }
                                    }}
                                    //className="mt-4"
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default AssistantChatConversation;
